import { Apollo } from 'src/gql/generated';

export type ActivityItemFragment = Apollo.ActivityItemFieldsFragment;
type ActivityBody = ActivityItemFragment['body'];

export type CommentActivityBody = Extract<ActivityBody, { __typename: 'CommentActivity' }>;
export type StatusUpdateActivityBody = Extract<
  ActivityBody,
  { __typename: 'StatusUpdateActivity' }
>;
export type AgentThreadActivityBody = Extract<ActivityBody, { __typename: 'AgentThreadActivity' }>;
/** The comment of a CommentActivity, non-null: the feed skips rows whose comment is null. */
export type ActivityComment = NonNullable<CommentActivityBody['comment']>;
/** The agent thread of an AgentThreadActivity, non-null: the feed skips rows whose thread is null. */
export type ActivityAgentThread = NonNullable<AgentThreadActivityBody['agentThread']>;

export function getIsCommentActivity(body: ActivityBody): body is CommentActivityBody {
  return body.__typename === 'CommentActivity';
}

export function getIsStatusUpdateActivity(body: ActivityBody): body is StatusUpdateActivityBody {
  return body.__typename === 'StatusUpdateActivity';
}

export function getIsAgentThreadActivity(body: ActivityBody): body is AgentThreadActivityBody {
  return body.__typename === 'AgentThreadActivity';
}

/** Coarse run-state of an AI thread, derived from its last assistant message (threads have no status field). */
export type AgentThreadRunState = 'running' | 'done' | 'error' | 'cancelled' | 'notStarted';

/**
 * Derives the row's run-state from the thread's last assistant message — mirrors the server's own
 * "last streaming assistant message" logic. STREAMING/CANCELLING ⇒ running (Stop available);
 * ERROR/CANCELLED ⇒ terminal; no assistant message at all ⇒ notStarted; otherwise done.
 */
export function getAgentThreadRunState(thread: ActivityAgentThread): AgentThreadRunState {
  const lastAssistant = [...thread.messages]
    .reverse()
    .find(message => message.role === Apollo.AgentMessageRole.Assistant);
  /**
   * No assistant message means the run never began: the server keeps the thread and activity rows
   * when the Temporal kickoff fails, leaving a thread that holds only the user prompt. Without
   * this, such threads would fall into the default bucket and render as done.
   */
  if (!lastAssistant) return 'notStarted';
  switch (lastAssistant.status) {
    case Apollo.AgentMessageStatus.Streaming:
    case Apollo.AgentMessageStatus.Cancelling:
      return 'running';
    case Apollo.AgentMessageStatus.Error:
      return 'error';
    case Apollo.AgentMessageStatus.Cancelled:
      return 'cancelled';
    default:
      return 'done';
  }
}
